import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Banknote, CheckCircle2, ChevronLeft, FileDown, MonitorSmartphone, Repeat2, Scissors, Trash2 } from "lucide-react";
import { SaleHeaderProps } from "../interfaces";
import { AuthContext } from "../../../../App";
import { getTranslatedSaleStatus } from "../../../../translates/sales/sales";
import { SaleStatus } from "../../../../interfaces/sales";
import { TableBillsStatus } from "../../../../interfaces/TableBills";
import { profiles } from "../../../../profiles";
import ToolTip from "../../../../components/ToolTip/ToolTip";

export const SaleHeader: React.FC<SaleHeaderProps> = ({
  canRefund,
  code,
  hasCashPayment,
  openRefundModal,
  openPartialCancelModal,
  openProductChangeModal,
  onConfirmCashPayment,
  onComplete,
  onDownloadPdf,
  isDownloadingPdf,
  status,
  isPendingExternalOrder,
  isTipAdjustment,
  bill,
  onSendToSession,
}) => {
  const { user, canMarkExpiredCancelledSalesAsPaid, canImportBills } = useContext(AuthContext);
  // YOSHI-11055: a pending external order has no bill, so no terminal session yet; whoever can import bills
  // can route it to one (the API applies the same permission)
  const canSendToSession: boolean = !!isPendingExternalOrder && canImportBills && !!onSendToSession;
  const canMarkAsPaid =
    (status === 0 || status === 1 || status === 5 || status === 7 || !!isPendingExternalOrder) &&
    canMarkExpiredCancelledSalesAsPaid;
  const canConfirmCashPayment: boolean =
    status === SaleStatus.PorConfirmar && hasCashPayment;
  // A tip-adjustment sale is cancelled by cancelling its original sale (the API rejects cancelling it
  // directly), so neither full nor partial cancellation is offered here.
  const canPartialCancel = status === 2 && user?.id_profile === profiles.superAdmin && !isTipAdjustment;
  // A sub-sale of a bill that is not paid yet cannot be cancelled on its own: the API rejects it so the
  // bill's money is never left behind.
  const belongsToUnpaidBill: boolean = !!bill && bill.status !== TableBillsStatus.PAID;
  // YOSHI-10261: corregir un producto mal marcado no mueve dinero, pero sí el inventario: solo para
  // los perfiles que ya pueden cancelar la venta. Una venta cancelada, reembolsada o expirada ya no
  // se corrige (es la misma regla que valida el backend).
  const canChangeProduct: boolean =
    (user?.id_profile === profiles.superAdmin || user?.id_profile === profiles.venuesAdmin) &&
    !isTipAdjustment &&
    status !== SaleStatus.Cancelada &&
    status !== SaleStatus.Reembolsada &&
    status !== SaleStatus["Reembolso pendiente"] &&
    status !== SaleStatus.Expirada;
  const canViewBillLink: boolean = user?.id_profile === profiles.superAdmin || user?.id_profile === profiles.venuesAdmin;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {`Venta ${code}`}
          </h1>
          <span
            className={`rounded-full px-3 py-1 text-center text-xs font-medium ${
              status === 0
                ? "bg-red-100 text-red-800"
                : status === 1
                  ? "bg-blue-100 text-blue-800"
                  : status === 2
                    ? "bg-green-100 text-green-800"
                    : status === 3
                      ? "bg-yellow-100 text-yellow-800"
                      : status === 4
                        ? "bg-red-100 text-red-800"
                        : status === 5
                          ? "bg-blue-100 text-blue-800"
                          : status === 6
                            ? "bg-gray-100 text-gray-800"
                            : status === 7
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
            }`}
          >
            {getTranslatedSaleStatus(status)}
          </span>
          {isTipAdjustment && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-center text-xs font-medium text-amber-800">
              Ajuste de propina
            </span>
          )}
        </div>
        {bill && canViewBillLink && (
          <Link
            to={`/panel/cuentas/${bill.uuid}`}
            className="flex w-fit items-center gap-1 text-sm font-medium transition-colors duration-300 hover:opacity-80 cursor-pointer"
            style={{ color: 'rgb(215 1 90)' }}
          >
            {`Cuenta ${bill.name}`}
          </Link>
        )}
        <Link
          to="/panel/ordenes"
          className="flex w-fit items-center gap-1 text-gray-500 transition-colors duration-300 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Volver a Órdenes</span>
        </Link>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {/*
          YOSHI-10444: the header already carries several labelled actions, so the export is an
          icon-only button with a tooltip and sits first, leaving the existing buttons where they are.
        */}
        <ToolTip text="Exportar PDF" placement="bottom">
          <button
            className="flex items-center rounded-md border border-gray-200 bg-white p-2.5 transition-colors duration-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onDownloadPdf}
            disabled={isDownloadingPdf}
            aria-label="Exportar PDF"
          >
            <FileDown className="h-4 w-4 shrink-0 stroke-gray-500" />
          </button>
        </ToolTip>
        {canConfirmCashPayment && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={onConfirmCashPayment}
          >
            <Banknote className="h-4 w-4 shrink-0 stroke-green-600" />
            <span className="text-sm font-medium text-green-600">
              Confirmar pago en efectivo
            </span>
          </button>
        )}
        {canSendToSession && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={onSendToSession}
          >
            <MonitorSmartphone className="h-4 w-4 shrink-0 stroke-pink-600" />
            <span className="text-sm font-medium text-pink-600">Mandar a sesión</span>
          </button>
        )}
        {canMarkAsPaid && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={onComplete}
          >
            <CheckCircle2 className="h-4 w-4 shrink-0 stroke-blue-600" />
            <span className="text-sm font-medium text-blue-600">Marcar como pagada</span>
          </button>
        )}
        {canPartialCancel && (
          <ToolTip
            text={bill ? "Esta venta pertenece a una cuenta. La cancelación parcial se gestiona desde la cuenta." : undefined}
            placement="bottom"
          >
            <button
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={openPartialCancelModal}
              disabled={!!bill}
            >
              <Scissors className="h-4 w-4 shrink-0 stroke-orange-600" />
              <span className="text-sm font-medium text-orange-600">
                Cancelación parcial
              </span>
            </button>
          </ToolTip>
        )}
        {canChangeProduct && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={openProductChangeModal}
          >
            <Repeat2 className="h-4 w-4 shrink-0 stroke-indigo-600" />
            <span className="text-sm font-medium text-indigo-600">
              Cambiar producto
            </span>
          </button>
        )}
        {canRefund && !isTipAdjustment && (
          <ToolTip
            text={
              belongsToUnpaidBill
                ? "Esta venta pertenece a una cuenta que aún no se paga. Quita sus productos o cancela la cuenta desde la cuenta."
                : undefined
            }
            placement="bottom"
          >
            <button
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={openRefundModal}
              disabled={belongsToUnpaidBill}
            >
              <Trash2 className="h-4 w-4 shrink-0 stroke-red-600" />
              <span className="text-sm font-medium text-red-600">
                Cancelar venta
              </span>
            </button>
          </ToolTip>
        )}
      </div>
    </div>
  );
};
